import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {Observable} from 'rxjs';
import * as moment from 'moment';
import * as _ from 'lodash';
import {InitializationAndConfigurationService} from './configuration/initializationAndConfiguration.service';
import {UserService} from './user.service';
import {UrlService} from './url.service';

@Injectable()
export class ClientService {
    private BASE_URL: string;

    constructor(private http: HttpClient,
                private userService: UserService,
                private initializationAndConfigurationService: InitializationAndConfigurationService,
                private urlService: UrlService) {
        this.BASE_URL = this.urlService.getBackendUrl() + 'data/clients';
    }

    getClient(clientId): any {
        return this.http.get(this.BASE_URL + '/' + clientId);
    }

    getAllClients(params: HttpParams): any {
        return this.http.get(this.BASE_URL, {params: params});
    }

    getAllClientsWorkload(params: HttpParams): Observable<any> {
        return this.http.get(this.BASE_URL + '/workload', {params: params});
    }

    getCaregivers(clientId, start, limit, sortField, sortOrder, filter) {
        let params = new HttpParams();
        if (start) {
            params = params.set('start', start);
        }
        if (limit) {
            params = params.set('limit', limit);
        }
        if (sortField) {
            params = params.set('sortField', sortField);
        }
        if (sortOrder) {
            params = params.set('sortOrder', sortOrder);
        }
        if (filter) {
            params = params.set('filter', JSON.stringify(filter));
        }

        const requestOptions = {
            params: params
        }

        return this.http.get(this.BASE_URL + '/' + clientId + '/caregivers', requestOptions);
    }

    createCaregiver(caregiver, clientId) {
        return this.http.post(this.BASE_URL + '/' + clientId + '/caregivers', caregiver);
    }

    updateCaregiver(caregiver, clientId) {
        return this.http.put(this.BASE_URL + '/' + clientId + '/caregivers/' + caregiver.id, caregiver);
    }

    addToWorkload(clients: any[]) {
        return this.http.post(this.BASE_URL + '/workload', clients);
    }

    removeFromWorkload(clients: any[]) {
        let params: HttpParams = new HttpParams();
        const clientsIds = [];
        clients.forEach((client) => {
            clientsIds.push(client.id);
        });

        params = params.set('clientsIds', clientsIds.join(','));

        const requestOptions = {
            params: params
        };

        return this.http.delete(this.BASE_URL + '/workload', requestOptions);
    }

    getClientPlacement(clientId) {
        return this.http.get(this.BASE_URL + '/' + clientId + '/placements');
    }

    getClientPlacementSingle(clientId, clientPlacementId) {
        return this.http.get(this.BASE_URL + '/' + clientId + '/placements/' + clientPlacementId);
    }

    getActiveInstrumentTypes(client, baseTypes) {
        let params = new HttpParams();

        params = params.set('baseTypes', baseTypes);

        const requestOptions = {
            params: params
        }

        return this.http.get(this.BASE_URL + '/' + client.id + '/activeInstruments', requestOptions);
    }

    getChronoline(clientId) {
        return this.http.get(this.BASE_URL + '/' + clientId + '/chronoline');
    }

    isCansOu(ouTags) {
        if (this.initializationAndConfigurationService.getApplicationConfiguration().cansOuTags === 'null') {
            return true;
        }
        let ret = false;
        _.every(ouTags, (tagCode) => {
            if (this.initializationAndConfigurationService.getApplicationConfiguration().cansOuTags.indexOf(tagCode) !== -1) {
                ret = true;
                return false; // Jump out of every()
            }
            return true;
        });
        return ret;
    }

    getVisibleOus(client, assessmentsCreationGraceDays, alwaysIncludeOus, cansOusOnly, instrumentType) {
        const ret = [];
        const usedPlacements = [];

        let cutoffDate = null;

        if (assessmentsCreationGraceDays != null
            && assessmentsCreationGraceDays !== -1) {
            cutoffDate = moment().subtract(assessmentsCreationGraceDays, 'day');
        }

        client.placements.forEach((placement) => {
            let useThisEndDate = null;
            if (placement.endDate != null && placement.adjustedEndDate != null) {
                useThisEndDate = placement.endDate > placement.adjustedEndDate ? placement.endDate : placement.adjustedEndDate;
            } else if (placement.endDate != null) {
                useThisEndDate = placement.endDate;
            } else {
                useThisEndDate = placement.adjustedEndDate;
            }

            if (useThisEndDate == null ||
                (cutoffDate != null && moment(useThisEndDate) >= cutoffDate) ||
                (placement.temporaryOpenDate != null && (moment(placement.temporaryOpenDate).add(3, 'days') >= moment()))) {
                if (this.userService.userIsOuResponsible(placement.orgUnit, this.initializationAndConfigurationService.getLoggedInUser())
                    || this.userService.userIsClientResponsible(client, this.initializationAndConfigurationService.getLoggedInUser())) {
                    if (usedPlacements[placement.orgUnit.code] == null) {
                        if (!cansOusOnly || this.isCansOu(placement.orgUnit.tagsCodes)) {

                            let add = true;
                            if (this.initializationAndConfigurationService.getApplicationConfiguration().instrumentTypeClientOUFilter !== 'null' && instrumentType != null) {
                                const instrumentTypeClientOUFilterMap = {};
                                _.each(this.initializationAndConfigurationService.getApplicationConfiguration()
                                    .instrumentTypeClientOUFilter.split('|'), (filter) => {
                                    const filterComponents = filter.split(':');
                                    instrumentTypeClientOUFilterMap[filterComponents[0]] = filterComponents[1] + ',' + filterComponents[2];
                                });

                                add = false;
                                const ouList = instrumentTypeClientOUFilterMap[instrumentType];

                                if (!ouList) {
                                    add = true;
                                }

                                if (add === false) {
                                    const filterList = ouList.split(',');

                                    if (filterList[0] === 'Include') {
                                        if (_.find(filterList, function (obj) {
                                            return obj === placement.orgUnit.code
                                        })) {
                                            add = true;
                                        }
                                    } else if (filterList[0] === 'Exclude') {
                                        if (_.find(filterList, function (obj) {
                                            return obj === placement.orgUnit.code
                                        })) {
                                            add = false;
                                        } else {
                                            add = true;
                                        }
                                    } else {
                                        // problem in the configuration
                                    }
                                }
                            }

                            if (add === true) {
                                ret.push(placement.orgUnit);
                                usedPlacements[placement.orgUnit.code] = placement;
                            }
                        }
                    }
                }
            }
        });

        // alwaysIncludeOus are probably cases where the assessment might already have an OU
        // coded, but that OU placement is closed.  In that case, if editing an Approved for
        // a closed placement that OU might be passed in, just so an empty list isn't returned.
        if (alwaysIncludeOus) {
            alwaysIncludeOus.forEach((ou) => {
                if (ou && usedPlacements[ou.code] == null) {
                    ret.push(ou);
                }
            });
        }

        return ret;
    }

    searchAllByIdentifierDobSsn(identifier: string, dob: any, lastSsn: string): Observable<any> {
        let params = new HttpParams();
        if (identifier) {
            params = params.set('identifier', identifier);
        }
        if (dob) {
            params = params.set('dob', moment(dob).format('YYYY-MM-DD'));
        }
        if (lastSsn) {
            params = params.set('lastSsn', lastSsn);
        }

        const requestOptions = {
            params: params
        };

        return this.http.get(this.BASE_URL + '/searchAllByIdentifierDobSsn', requestOptions);
    }

    claimClientAccess(clientId: number, organizationId: number): Observable<any> {
        // const params = {
        //     clientId: clientId,
        //     organizationId: organizationId
        // };
        const params = new HttpParams()
            .set('clientId', clientId.toString())
            .set('organizationId', organizationId.toString());
        return this.http.post(this.BASE_URL + '/claimClientAccess', {}, { params });
    }
}
